import { useQuery } from '@tanstack/react-query';

import config from '@/config';
import { I18nWording } from '@/services/i18n';

/**
 * Checks the /health endpoint and redirects to the /health page if the API is unhealthy.
 */
export async function checkHealthAndRedirect() {
  if (!window.location.pathname.startsWith('/health')) {
    console.warn('Session retrieval returned 503, checking /health API endpooint...');

    const href =
      '/health?redirect=' +
      encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);

    await window
      .fetch(`${config.apiUrl}/health`)
      .then((res) => {
        if (!res.ok) {
          console.error('Health check failed, redirecting to /health page...');

          window.location.href = href;
        }
      })
      .catch(() => {
        console.error('Health check did not respond, redirecting to /health page...');

        window.location.href = href;
      });
  }
}

type Health =
  | {
      status: 'ok';
    }
  | {
      status: 'maintenance';
      endOfMaintenanceETA: string;
    }
  | {
      status: 'unavailable';
      message?: I18nWording;
    }
  | {
      status: 'unknown'; // Used as initial state
    };

export const useHealth = () => {
  let initialData = { status: 'unknown' } as Health;

  if (localStorage.getItem('health')) {
    try {
      const parsed = JSON.parse(localStorage.getItem('health') as string) as Health;

      if (
        parsed &&
        typeof parsed === 'object' &&
        'status' in parsed &&
        ['maintenance', 'unavailable'].includes(parsed.status)
      ) {
        initialData = parsed;
      }
    } catch {
      // Ignore parsing errors
    }
  }

  return useQuery({
    queryKey: ['health'],
    queryFn: async () =>
      fetch(`${config.apiUrl}/health`, {
        headers: {
          'Content-Type': 'application/json',
          'App-Version': config.appVersion,
          'Health-Ignore': config.health.ignore ? '1' : '0',
        },
      })
        .then((res) => res.json())
        .then((health: Health) => {
          localStorage.setItem('health', JSON.stringify(health));

          return health;
        }),
    initialData,
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
    refetchIntervalInBackground: true,
    networkMode: 'always',
  });
};

// @ts-ignore - Enable to bypass health check for debugging purposes
window.bypassHealthCheck = (value: boolean = true) => {
  localStorage.setItem('health:ignore', value.toString());
  window.location.reload();
};
